/**
 * Medical Leaves Form Dialog Component
 * Dialog for creating and editing medical leaves
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  MedicalLeave,
  MedicalLeaveFormData,
  EmployeeForLeave,
  calculateTotalDays,
} from '../../../types/medicalLeave';
import { LeaveReasonSelect } from './LeaveReasonSelect';
import { CidAutocomplete } from './CidAutocomplete';
import { CertificateUpload } from './CertificateUpload';
import { medicalLeaveService } from '../../../services/medicalLeaveService';

interface MedicalLeavesFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  medicalLeave?: MedicalLeave | null;
  employee?: EmployeeForLeave | null;
}

export const MedicalLeavesFormDialog: React.FC<MedicalLeavesFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  medicalLeave,
  employee,
}) => {
  const isEditing = Boolean(medicalLeave?.id);

  // Form state
  const [formData, setFormData] = useState<MedicalLeaveFormData>({
    employeeId: 0,
    leaveReason: '',
    doctorName: '',
    doctorUf: '',
    doctorCrm: '',
    cidCode: '',
    cidDescription: '',
    startDate: '',
    endDate: '',
    returnRestrictions: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalDays, setTotalDays] = useState<number>(0);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);

  // Initialize form data
  useEffect(() => {
    if (isEditing && medicalLeave) {
      setFormData({
        employeeId: medicalLeave.employeeId,
        leaveReason: medicalLeave.leaveReason,
        doctorName: medicalLeave.doctorName,
        doctorUf: medicalLeave.doctorUf || '',
        doctorCrm: medicalLeave.doctorCrm,
        cidCode: medicalLeave.cidCode,
        cidDescription: medicalLeave.cidDescription,
        startDate: medicalLeave.startDate.split('T')[0],
        endDate: medicalLeave.endDate.split('T')[0],
        returnRestrictions: medicalLeave.returnRestrictions || '',
      });
    } else if (employee) {
      setFormData({
        employeeId: employee.id,
        leaveReason: '',
        doctorName: '',
        doctorUf: '',
        doctorCrm: '',
        cidCode: '',
        cidDescription: '',
        startDate: '',
        endDate: '',
        returnRestrictions: '',
      });
    }
  }, [isEditing, medicalLeave, employee]);

  // Calculate total days when dates change
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const days = calculateTotalDays(formData.startDate, formData.endDate);
      setTotalDays(days);
    } else {
      setTotalDays(0);
    }
  }, [formData.startDate, formData.endDate]);

  const handleChange = (field: keyof MedicalLeaveFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCidChange = (code: string, description: string) => {
    setFormData((prev) => ({
      ...prev,
      cidCode: code,
      cidDescription: description,
    }));
  };

  const handleCertificateUpload = async (file: File) => {
    setCertificateFile(file);
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!formData.employeeId) {
      setError('Funcionário é obrigatório');
      return;
    }
    if (!formData.leaveReason) {
      setError('Motivo do afastamento é obrigatório');
      return;
    }
    if (!formData.doctorName) {
      setError('Nome do médico é obrigatório');
      return;
    }
    if (!formData.doctorCrm || !/^\d{4,6}(\/[A-Z]{2})?$/.test(formData.doctorCrm)) {
      setError('CRM inválido. Use o formato 123456 ou 123456/SP');
      return;
    }
    if (!formData.cidCode) {
      setError('Código CID é obrigatório');
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      setError('Data de início e fim são obrigatórias');
      return;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setError('Data fim deve ser maior ou igual à data início');
      return;
    }

    try {
      setLoading(true);

      let savedLeave: MedicalLeave;

      if (isEditing && medicalLeave) {
        savedLeave = await medicalLeaveService.updateMedicalLeave(medicalLeave.id, formData);
      } else {
        savedLeave = await medicalLeaveService.createMedicalLeave(formData);
      }

      // Upload certificate if provided
      if (certificateFile && savedLeave.id) {
        await medicalLeaveService.uploadCertificate(savedLeave.id, certificateFile);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving medical leave:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao salvar licença médica');
    } finally {
      setLoading(false);
    }
  };

  const displayEmployee = isEditing
    ? {
        name: medicalLeave?.employeeName || '',
        companyName: medicalLeave?.companyName || '',
        departmentName: medicalLeave?.departmentName || '',
        occupationName: medicalLeave?.occupationName || '',
      }
    : {
        name: employee?.name || '',
        companyName: employee?.companyName || '',
        departmentName: employee?.departmentName || '',
        occupationName: employee?.occupationName || '',
      };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {isEditing ? 'Editar Licença Médica' : 'Nova Licença Médica'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Employee Info (Readonly) */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            Funcionário
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Nome"
                value={displayEmployee.name}
                InputProps={{ readOnly: true }}
                variant="filled"
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Empresa"
                value={displayEmployee.companyName}
                InputProps={{ readOnly: true }}
                variant="filled"
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Setor"
                value={displayEmployee.departmentName}
                InputProps={{ readOnly: true }}
                variant="filled"
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Cargo"
                value={displayEmployee.occupationName}
                InputProps={{ readOnly: true }}
                variant="filled"
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* Leave Info */}
        <Typography variant="subtitle2" gutterBottom color="primary">
          Dados do Afastamento
        </Typography>
        <Grid container spacing={2}>
          {/* Leave Reason */}
          <Grid size={12}>
            <LeaveReasonSelect
              value={formData.leaveReason}
              onChange={(value) => handleChange('leaveReason', value)}
              required
              disabled={loading}
            />
          </Grid>

          {/* Doctor Info */}
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              label="Nome do Médico"
              value={formData.doctorName}
              onChange={(e) => handleChange('doctorName', e.target.value)}
              required
              disabled={loading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="CRM"
              value={formData.doctorCrm}
              onChange={(e) => {
                let val = e.target.value.toUpperCase();
                // Allow digits, one slash, and up to 2 letters after slash
                val = val.replace(/[^0-9/A-Z]/g, '');
                // Enforce format: up to 6 digits, optional /UF
                const match = val.match(/^(\d{0,6})(\/?)([A-Z]{0,2})$/);
                if (match) {
                  val = match[1] + (match[2] || (match[3] ? '/' : '')) + match[3];
                } else {
                  val = val.replace(/[^0-9]/g, '').slice(0, 6);
                }
                handleChange('doctorCrm', val);
              }}
              required
              disabled={loading}
              placeholder="123456/SP"
              helperText="Ex: 123456 ou 123456/SP"
              inputProps={{ maxLength: 9 }}
            />
          </Grid>

          {/* CID */}
          <Grid size={{ xs: 12, md: 4 }}>
            <CidAutocomplete
              value={formData.cidCode}
              onChange={handleCidChange}
              required
              disabled={loading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              label="Descrição do CID"
              value={formData.cidDescription}
              InputProps={{ readOnly: true }}
              variant="filled"
              helperText="Preenchido automaticamente ao selecionar o CID"
            />
          </Grid>

          {/* Dates */}
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
              required
              disabled={loading}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleChange('endDate', e.target.value)}
              required
              disabled={loading}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Total de Dias"
              value={totalDays > 0 ? `${totalDays} dias` : '-'}
              InputProps={{ readOnly: true }}
              variant="filled"
            />
          </Grid>

          {/* Return Restrictions */}
          <Grid size={12}>
            <TextField
              fullWidth
              label="Restrições de Retorno"
              value={formData.returnRestrictions}
              onChange={(e) => handleChange('returnRestrictions', e.target.value)}
              multiline
              rows={3}
              disabled={loading}
              helperText="Descreva quaisquer restrições ao retornar ao trabalho (opcional)"
            />
          </Grid>

          {/* Certificate Upload */}
          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mt: 1 }}>
              Atestado Médico
            </Typography>
            <CertificateUpload
              onUpload={handleCertificateUpload}
              hasCertificate={Boolean(certificateFile) || Boolean(medicalLeave?.hasCertificate)}
              certificateFileName={certificateFile?.name}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {isEditing ? 'Atualizar' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalLeavesFormDialog;
